import { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Mail, Phone, Send, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { publicApi } from '@/lib/api';
import { useSiteContent } from '@/lib/useSiteContent';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { tx } = useSiteContent();
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    
    try {
      await publicApi.submitContact(formData);
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      console.error('Error submitting contact:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="pt-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32" data-testid="contact-hero">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="text-muted-foreground text-sm tracking-[0.2em] uppercase mb-4">
              Get In Touch
            </p>
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight leading-[1.1] mb-6">
              {tx('contact.hero.heading', 'Contact Us')}
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed">
              {tx('contact.hero.subtext', "Have a project in mind or want to learn more about our developments? We'd love to hear from you. Fill out the form below and our team will get back to you shortly.")}
            </p>
          </motion.div>
        </div>
      </section>
      
      {/* Contact Form & Info */}
      <section className="pb-20 md:pb-32" data-testid="contact-form-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
            {/* Contact Form */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="md:col-span-7"
            >
              {submitted ? (
                <div className="bg-secondary/50 p-12 text-center" data-testid="success-message">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-semibold mb-4">Thank You!</h3>
                  <p className="text-muted-foreground mb-8">
                    Your message has been sent successfully. Our team will review your 
                    enquiry and get back to you within 1-2 business days.
                  </p>
                  <Button 
                    onClick={() => {
                      setSubmitted(false);
                      setFormData({ name: '', email: '', phone: '', message: '' });
                    }}
                    variant="outline"
                    className="rounded-full"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-8" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Smith"
                        className="h-12 border-border/60 focus:border-primary"
                        required
                        data-testid="input-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        className="h-12 border-border/60 focus:border-primary"
                        required
                        data-testid="input-email"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number (Optional)</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+64 21 123 4567"
                      className="h-12 border-border/60 focus:border-primary"
                      data-testid="input-phone"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="message">Your Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your project or enquiry..."
                      rows={6}
                      className="border-border/60 focus:border-primary resize-none"
                      required
                      data-testid="input-message"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    size="lg" 
                    className="rounded-full px-10"
                    disabled={loading}
                    data-testid="submit-button"
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <Send className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </motion.div>
            
            {/* Contact Info */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="md:col-span-5"
            >
              <div className="bg-primary text-primary-foreground p-8 md:p-10 sticky top-28">
                <h3 className="text-2xl font-semibold mb-8">Contact Information</h3>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary-foreground/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-medium mb-1">Location</p>
                      <p className="text-primary-foreground/70 text-sm whitespace-pre-line">
                        {tx('contact.info.address_line1', '684 Tremaine Ave, Level 1')}{'\n'}
                        {tx('contact.info.address_line2', 'Palmerston North, New Zealand')}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary-foreground/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-medium mb-1">Email</p>
                      <a 
                        href={`mailto:${tx('contact.info.email', 'info@koroliving.co.nz')}`}
                        className="text-primary-foreground/70 text-sm hover:text-primary-foreground transition-colors"
                      >
                        {tx('contact.info.email', 'info@koroliving.co.nz')}
                      </a>
                    </div>
                  </div>
                  
                  {tx('contact.info.phone', '') && (
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-primary-foreground/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Phone className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-medium mb-1">Phone</p>
                        <a 
                          href={`tel:${tx('contact.info.phone', '').replace(/\s/g, '')}`}
                          className="text-primary-foreground/70 text-sm hover:text-primary-foreground transition-colors"
                        >
                          {tx('contact.info.phone', '')}
                        </a>
                      </div>
                    </div>
                  )}
                </div>
                
                <hr className="border-primary-foreground/10 my-8" />
                
                <div>
                  <p className="font-medium mb-2">Business Hours</p>
                  <p className="text-primary-foreground/70 text-sm whitespace-pre-line">
                    {tx('contact.info.hours', 'Monday – Friday: 9:00 AM – 5:00 PM')}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Map Section (Optional placeholder) */}
      <section className="bg-secondary/50" data-testid="map-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20">
          <div className="text-center">
            <h2 className="text-3xl font-semibold mb-4">Our Location</h2>
            <p className="text-muted-foreground mb-8">
              Based in Manawatu, serving the wider North Island region.
            </p>
            <div className="aspect-video bg-secondary rounded-lg overflow-hidden">
              <iframe
                src="https://www.google.com/maps?q=684+Tremaine+Avenue,+Palmerston+North,+New+Zealand&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Koro Living Location"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
